import type { JobUpsertParams } from '../../../types/job';
import type { BudgetRange } from '../../../types/quote';
import type { City } from '../../../types/profile';
import type { ServiceCategory } from '../../../types/service';

const SESSION_PREFIX = 'pigeons.job-creation.v1';
const SESSION_TTL_MS = 24 * 60 * 60 * 1000;

export type JobCreationSessionParams = Omit<
  JobUpsertParams,
  'city' | 'budgetRange' | 'requiredServices'
> & {
  city: City | null;
  budgetRange: BudgetRange | null;
  requiredServices: ServiceCategory[];
};

export interface JobCreationSession {
  version: 1;
  savedAt: number;
  step: number;
  params: JobCreationSessionParams;
  expiresAt: string;
  selectionDate: string;
  listingTypeId: number | null;
  jobId: string | null;
  fingerprint: string | null;
  publicationRequestId: string;
}

const storageKey = (userId: string): string => `${SESSION_PREFIX}.${userId}`;

export const createPublicationFingerprint = (
  jobId: string,
  listingTypeId: number,
  expiresAt: string,
  selectionDate: string
): string => [jobId, listingTypeId, expiresAt, selectionDate].join('|');

export const readJobCreationSession = (userId: string): JobCreationSession | null => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.sessionStorage.getItem(storageKey(userId));
    if (!stored) return null;
    const parsed = JSON.parse(stored) as JobCreationSession;
    if (parsed.version !== 1 || Date.now() - parsed.savedAt > SESSION_TTL_MS) {
      window.sessionStorage.removeItem(storageKey(userId));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const writeJobCreationSession = (userId: string, session: JobCreationSession): void => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(
    storageKey(userId),
    JSON.stringify({ ...session, savedAt: Date.now() })
  );
};

export const discardJobCreationSession = (userId: string): void => {
  if (typeof window !== 'undefined') window.sessionStorage.removeItem(storageKey(userId));
};
